<script lang="ts">
    import ChevronDownIcon from '@amp/web-app-components/assets/icons/chevron-down.svg';
    import { createEventDispatcher } from 'svelte';

    type T = $$Generic;

    const dispatch = createEventDispatcher<{ select: T }>();

    export let selectedOption: T;
    export let options: T[] = [];

    /**
     * Whether the popover should inherit typography from parent, or use default typography.
     */
    export let inheritTypography = false;

    function onValueChanged() {
        if (selectedOption) {
            dispatch('select', selectedOption);
        }
    }
</script>

<div
    class="select-wrapper"
    class:select-wrapper--inherited-typography={inheritTypography}
    data-testid="accessory-button-select-wrapper"
>
    <!-- svelte-ignore a11y-no-onchange -->
    <select
        class="select"
        data-testid="accessory-button-select"
        bind:value={selectedOption}
        on:change={onValueChanged}
    >
        {#each options as selectOption}
            <option value={selectOption} data-testid="select-option">
                <!-- Note: using default slot for both option and select-text caused SSR error:
                    "ReferenceError: selectOption is not defined" when reloading the page -->
                <slot name="label" option={selectOption} />
            </option>
        {/each}
    </select>

    <div
        class="select-text"
        aria-hidden="true"
        data-testid="accessory-button-select-text"
    >
        {#if $$slots['select-text']}
            <slot name="select-text" {selectedOption} />
        {:else}
            <slot name="label" option={selectedOption} />
        {/if}
    </div>

    <div class="select-chevron" role="presentation" aria-hidden="true">
        <slot name="select-chevron">
            <ChevronDownIcon aria-hidden="true" />
        </slot>
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;

    .select-wrapper {
        position: relative;
        display: grid;
        grid-template-columns: auto auto;
        gap: 4px;
        z-index: var(--z-default);

        &:not(.select-wrapper--inherited-typography) {
            color: var(--keyColor);
            font: var(--title-3-tall);
        }
    }

    .select {
        padding: 8px;
        appearance: none;
        border: none;
        max-width: 100%; // Prevent focus ring overflow in Firefox
        background: transparent; // Improves Dark Mode transitions
        color: transparent; // Hide text when dropdown isn't open -- selected option is displayed by `.select-text`
        font: var(--title-3); // Font size used in dropdown

        @include absolute-center; // Expands element to fit .select-wrapper

        @include target-chrome {
            // Align `1px` gap between select baseline and heading baseline
            transform: translateY(1px);
        }
    }

    .select-text {
        grid-area: 1 / 1;
        white-space: nowrap;
        color: inherit;
        font: inherit;
    }

    .select-chevron {
        grid-area: 1 / 2;
        width: 9px;
        height: 5px;
        margin-top: 10px;
        fill: var(--keyColor);

        :global(svg) {
            display: block;
        }
    }

    option {
        color: var(--systemPrimary);
        background-color: var(--pageBG);
    }
</style>
